import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DefaultHomecard } from '@spotahome/ui-library';
import { useSoyuzFeatureToggle } from '@spotahome/soyuz/client';
import Tracking from '@spotahome/soyuz-tracking';

import useFavorite from '../../utils/useFavorite';
import { getRentableUnitPrice } from '../../utils/paymentsAdapter';
import {
  getHomecardImageSet,
  getRatingFromReviews,
  getUrlWithMovingDates,
  getChips
} from '../../utils/homecards';

import { generateHomeCardLabels } from './generateHomeCardLabels';

const getHomecardClasses = (visited, selected, tracking) =>
  classNames(
    'home-card',
    'is-animated',
    {
      'home-card--visited': visited,
      'home-card--selected': selected
    },
    tracking
  );

const Homecard = ({
  data: homecard,
  selected,
  tracking,
  onDelete,
  pageType,
  section,
  ...props
}) => {
  const seasonalPriceRange = useSoyuzFeatureToggle('seasonalPriceRange');

  const { isFavorite, toggleFavorite } = useFavorite({
    id: homecard.id,
    isFavorite: homecard.isFavorite,
    onToggleFavorite: props.onToggleFavorite,
    section,
    pageType
  });

  const handleMouseEnter = () => {
    props.onCardMouseEnter(homecard.id);
  };

  const handleMouseLeave = () => {
    props.onCardMouseLeave(homecard.id);
  };

  const classes = getHomecardClasses(homecard.visited, selected, tracking);

  const price = seasonalPriceRange
    ? getRentableUnitPrice(
        homecard.monthlyPrice.type,
        homecard.monthlyPrice.minimumPrice,
        homecard.currencyIsoCode,
        homecard.monthlyPrice.pricesByMonth,
        homecard.monthlyPrice.fixedPrice,
        seasonalPriceRange,
        { moveIn: props.moveIn, moveOut: props.moveOut },
        {
          flexibleMoveIn: props.flexibleMoveIn,
          flexibleMoveOut: props.flexibleMoveOut
        }
      )
    : {
        type: homecard.monthlyPrice.type,
        amount: homecard.monthlyPrice.minimumPrice,
        currencyCode: homecard.currencyIsoCode,
        isSeasonalPriceFeatureOn: seasonalPriceRange
      };

  const homecardBottomChips = getChips(
    homecard.noSecurityDeposit,
    props.freeCancellationDeadline.freeCancellationCurrently
      ? props.freeCancellationDeadline
      : null,
    homecard.features,
    props.showBillsIncludedLabel,
    homecard.offers,
    homecard.currencyIsoCode,
    homecard.allowsPermanentContract,
    homecard.isBestChoice,
    homecard.isInstantBooking
  );

  const onMoreInfoClickHandler = () => {
    Tracking.ga.sendEvent('Search', 'more-info', homecard.id);
  };

  return (
    <div
      className={classes}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DefaultHomecard
        disabled
        price={price}
        isAnimatedFavorite={props.isAnimatedFavorite}
        handleFavourite={toggleFavorite}
        onDelete={onDelete}
        availableFrom={homecard.firstAvailableDate}
        image={homecard.mainPhotoUrl}
        imageSet={
          homecard.photoUrls ? getHomecardImageSet(homecard.photoUrls) : ''
        }
        isFavorite={isFavorite}
        favourite={isFavorite} // TODO: review if necessary
        link={getUrlWithMovingDates(homecard.url, props.moveIn, props.moveOut)}
        onClick={props.onClick}
        onMouseEnter={handleMouseEnter} // TODO: this might be treated in the patternLibrary
        onMouseLeave={handleMouseLeave} // TODO: this might be treated in the patternLibrary
        overflow={props.overflow}
        rating={getRatingFromReviews(homecard.reviews)}
        renderLabels={generateHomeCardLabels(homecard)}
        selected={props.selected} // TODO: this might be treated in the patternLibrary
        title={homecard.title}
        type={homecard.type}
        verified={homecard.checked}
        hasLazyLoad={props.hasLazyLoad}
        isSelect={homecard.isSelect}
        renderExtraContent={props.renderExtraContent}
        showAvailableFrom={props.showAvailableFrom}
        showAvailableFromYear={props.showAvailableFromYear}
        showPrice={props.showPrice}
        newTab={props.newTab}
        photos={homecard.photos}
        isFullTitleDesign
        lazyCarousel
        chips={homecardBottomChips}
        withTransparentBackground={props.withTransparentBackground}
        isShowStatus={props.isShowStatus}
        onMoreInfoClick={onMoreInfoClickHandler}
      />
    </div>
  );
};

Homecard.propTypes = {
  data: PropTypes.shape({
    adId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    checked: PropTypes.bool,
    id: PropTypes.string,
    isFavorite: PropTypes.bool,
    mainPhotoUrl: PropTypes.string,
    monthlyPrice: PropTypes.shape({
      type: PropTypes.string,
      minimumPrice: PropTypes.number,
      pricesByMonth: PropTypes.shape({
        [PropTypes.string]: PropTypes.number
      }),
      fixedPrice: PropTypes.number
    }),
    currencyIsoCode: PropTypes.string,
    features: PropTypes.shape({ [PropTypes.string]: PropTypes.bool }),
    noSecurityDeposit: PropTypes.bool,
    photos: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string })),
    photoUrls: PropTypes.shape({
      homecardHidpi: PropTypes.string,
      homecard: PropTypes.string
    }),
    reviews: PropTypes.shape({
      ratingAverage: PropTypes.number
    }),
    title: PropTypes.string,
    type: PropTypes.string,
    url: PropTypes.string,
    visited: PropTypes.bool,
    allowsPermanentContract: PropTypes.bool,
    firstAvailableDate: PropTypes.string,
    offers: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  disabled: PropTypes.bool,
  freeCancellationDeadline: PropTypes.shape({
    freeCancellationCurrently: PropTypes.bool,
    freeCancellationDeadline: PropTypes.string
  }),
  isFreeCancellationVariant: PropTypes.bool,
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  flexibleMoveIn: PropTypes.bool,
  flexibleMoveOut: PropTypes.bool,
  onCardMouseEnter: PropTypes.func,
  onCardMouseLeave: PropTypes.func,
  onToggleFavorite: PropTypes.func,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  overflow: PropTypes.bool,
  selected: PropTypes.bool,
  tracking: PropTypes.string,
  hasLazyLoad: PropTypes.bool,
  renderExtraContent: PropTypes.func,
  showBillsIncludedLabel: PropTypes.bool,
  showAvailableFrom: PropTypes.bool,
  newTab: PropTypes.bool,
  showPrice: PropTypes.bool,
  zoomOnHover: PropTypes.bool,
  isAnimatedFavorite: PropTypes.bool,
  showAvailableFromYear: PropTypes.bool,
  withTransparentBackground: PropTypes.bool,
  pageType: PropTypes.string,
  section: PropTypes.string,
  isShowStatus: PropTypes.bool
};

Homecard.defaultProps = {
  disabled: false,
  moveIn: '',
  moveOut: '',
  flexibleMoveIn: false,
  flexibleMoveOut: false,
  onCardMouseEnter: () => {},
  onCardMouseLeave: () => {},
  onToggleFavorite: () => {},
  onDelete: null,
  onClick: () => {},
  overflow: false,
  selected: false,
  tracking: '',
  freeCancellationDeadline: {},
  isFreeCancellationVariant: false,
  hasLazyLoad: false,
  renderExtraContent: () => {},
  showBillsIncludedLabel: false,
  showAvailableFrom: true,
  newTab: true,
  showPrice: true,
  zoomOnHover: false,
  isAnimatedFavorite: false,
  showAvailableFromYear: false,
  withTransparentBackground: false,
  pageType: '',
  section: '',
  isShowStatus: false
};

export default Homecard;
